import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { schedulerApi, SchedulerRule, SchedulerRuleWithoutId } from '../api';
import { useAuthContext } from '../context';

interface UseSchedulerRuleManagerParams {
  limit?: number;
  hideHealthSummary?: boolean;
}

interface CreateSchedulerRuleParams {
  content: string;
  source?: string;
  tags?: string[];
}

interface UpdateSchedulerRuleParams {
  ruleId: string;
  content: string;
  source?: string;
  tags?: string[];
}

const schedulerRulesQueryKey = 'lifestack.scheduler.rules';

export const useSchedulerRuleManager = ({
  limit,
  hideHealthSummary = true,
}: UseSchedulerRuleManagerParams = {}) => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  // Get all scheduler rules
  const {
    data: schedulerRules = [],
    isLoading: isLoadingRules,
    error: rulesError,
    refetch: refetchRules,
  } = useQuery({
    queryKey: [schedulerRulesQueryKey, user.userId, limit, hideHealthSummary],
    queryFn: () =>
      schedulerApi
        .getSchedulerRules(user.userId!, limit, hideHealthSummary)
        .then(({ data }) => data),
    enabled: !!user.userId,
  });

  // Create scheduler rule mutation
  const createRuleMutation = useMutation({
    mutationFn: ({ content, source, tags }: CreateSchedulerRuleParams) => {
      const ruleData: SchedulerRuleWithoutId = {
        content,
        source,
        tags,
      };
      return schedulerApi.createSchedulerRule(user.userId!, ruleData).then(({ data }) => data);
    },
    onSuccess: () => {
      // Invalidate and refetch scheduler rules
      queryClient.invalidateQueries({ queryKey: [schedulerRulesQueryKey] });
    },
  });

  // Update scheduler rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: ({ ruleId, content, source, tags }: UpdateSchedulerRuleParams) => {
      const ruleData: SchedulerRuleWithoutId = {
        content,
        source,
        tags,
      };
      return schedulerApi
        .updateSchedulerRule(user.userId!, ruleId, ruleData)
        .then(({ data }) => data);
    },
    onSuccess: () => {
      // Invalidate and refetch scheduler rules
      queryClient.invalidateQueries({ queryKey: [schedulerRulesQueryKey] });
    },
  });

  // Delete scheduler rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: string) => {
      return schedulerApi.deleteSchedulerRule(user.userId!, ruleId).then(({ data }) => data);
    },
    onSuccess: () => {
      // Invalidate and refetch scheduler rules
      queryClient.invalidateQueries({ queryKey: [schedulerRulesQueryKey] });
    },
  });

  // Helper functions
  const createSchedulerRule = useCallback(
    async (params: CreateSchedulerRuleParams): Promise<SchedulerRule> => {
      return createRuleMutation.mutateAsync(params);
    },
    [createRuleMutation]
  );

  const updateSchedulerRule = useCallback(
    async (params: UpdateSchedulerRuleParams): Promise<SchedulerRule> => {
      return updateRuleMutation.mutateAsync(params);
    },
    [updateRuleMutation]
  );

  const deleteSchedulerRule = useCallback(
    async (ruleId: string): Promise<any> => {
      return deleteRuleMutation.mutateAsync(ruleId);
    },
    [deleteRuleMutation]
  );

  const getSchedulerRuleById = useCallback(
    (ruleId: string): SchedulerRule | undefined => {
      return schedulerRules.find((rule) => rule.ruleId === ruleId);
    },
    [schedulerRules]
  );

  return {
    // Data
    schedulerRules,
    isLoadingRules,
    rulesError,

    // Mutations
    createSchedulerRule,
    updateSchedulerRule,
    deleteSchedulerRule,

    // Mutation states
    isCreatingRule: createRuleMutation.isPending,
    isUpdatingRule: updateRuleMutation.isPending,
    isDeletingRule: deleteRuleMutation.isPending,

    // Mutation errors
    createRuleError: createRuleMutation.error,
    updateRuleError: updateRuleMutation.error,
    deleteRuleError: deleteRuleMutation.error,

    // Helper functions
    getSchedulerRuleById,
    refetchRules,
  };
};
